import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Card, 
  CardContent, 
  Divider, 
  Button, 
  Chip, 
  useTheme,
  Stack,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Tooltip
} from '@mui/material';
import { 
  TrendingUp, 
  TrendingDown, 
  Warning, 
  ShowChart, 
  ArrowForward, 
  Info, 
  Timeline,
  ArrowUpward,
  ArrowDownward,
  CompareArrows,
  MonetizationOn,
  AttachMoney,
  BarChart,
  CalendarMonth,
  HelpOutline,
  Business,
  BarChart as MarketCapIcon,
  Category as SectorIcon,
  Lightbulb as RecommendationsIcon,
  ReportProblem,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import { getSignalColor } from '../../theme/themesV3';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
  }
}));

// Styled tabs to match the style in MarketAnalysis
const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(2),
  '& .MuiTab-root': {
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '0.95rem',
  }
}));


//DELETE THIS
// const TrendBadge = styled(Box)<{ status: string }>(({ theme, status }) => {
//   let bgColor = theme.palette.grey[500];
//   let textColor = theme.palette.common.white;
  
//   if (status.toLowerCase().includes('positive') || 
//       status.toLowerCase().includes('improving') || 
//       status.toLowerCase().includes('inflow') || 
//       status.toLowerCase().includes('low')) {
//     bgColor = theme.palette.success.main;
//   } else if (status.toLowerCase().includes('negative') || 
//              status.toLowerCase().includes('deteriorating') || 
//              status.toLowerCase().includes('outflow') || 
//              status.toLowerCase().includes('high')) {
//     bgColor = theme.palette.error.main;
//   } else if (status.toLowerCase().includes('neutral') || 
//              status.toLowerCase().includes('steady') || 
//              status.toLowerCase().includes('medium')) {
//     bgColor = theme.palette.warning.main;
//     textColor = theme.palette.common.black;
//   }
  
//   return {
//     display: 'inline-flex',
//     alignItems: 'center',
//     padding: theme.spacing(0.5, 1.5),
//     borderRadius: theme.shape.borderRadius,
//     backgroundColor: bgColor,
//     color: textColor,
//     fontWeight: 'bold',
//     fontSize: '0.875rem'
//   };
// });

// Define props
interface MarketTrendsViewProps {
  marketReport: any;
  onTickerSelect?: (ticker: string) => void;
  renderMarketRating: (rating: string) => React.ReactElement;
  renderMarketTrend: (trend: string) => React.ReactElement;
  onTabChange: (index: number, subtab?: number) => void;
  initialTab?: number;
  view?: string;
}

// Tab panel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`trend-tabpanel-${index}`}
      aria-labelledby={`trend-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `trend-tab-${index}`,
    'aria-controls': `trend-tabpanel-${index}`,
  };
};

const MarketTrendsView: React.FC<MarketTrendsViewProps> = ({ 
  marketReport, 
  onTickerSelect,
  renderMarketRating,
  renderMarketTrend,
  onTabChange,
  initialTab,
  view
}) => {
  const theme = useTheme();
  
  // Add state variables to store the data from marketReport
  const [performanceData, setPerformanceData] = useState<any>({});
  const [riskData, setRiskData] = useState<any>({});
  const [moneyFlowData, setMoneyFlowData] = useState<any>({});
  
  // Extract data from the marketReport in useEffect
  useEffect(() => {
    if (marketReport) {
      const trendReports = marketReport?.trend_reports || {};
      
      // Extract data for the different tabs
      const perfData = trendReports?.Performance || {};
      const riskD = trendReports?.Risk || {};
      const moneyFlowD = trendReports?.['Money Flow'] || {};
      
      setPerformanceData(perfData);
      setRiskData(riskD);
      setMoneyFlowData(moneyFlowD);
    }
  }, [marketReport]);

  // Helper function to format percentage
  const formatPercent = (value: number | undefined) => {
    if (value === undefined || value === null) return 'N/A';
    return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  // Helper function to get trend icon
  const getTrendIcon = (trend: string) => {
    if (!trend) return null;
    
    const trendLower = trend.toLowerCase();
    if (trendLower.includes('improving') || trendLower.includes('positive') || trendLower.includes('inflow')) {
      return <ArrowUpward fontSize="small" sx={{ color: getSignalColor(trend), mr: 0.5 }} />;
    } else if (trendLower.includes('deteriorating') || trendLower.includes('negative') || trendLower.includes('outflow')) {
      return <ArrowDownward fontSize="small" sx={{ color: getSignalColor(trend), mr: 0.5 }} />;
    } else if (trendLower.includes('steady') || trendLower.includes('neutral') || trendLower.includes('flat')) {
      return <CompareArrows fontSize="small" sx={{ color: getSignalColor(trend), mr: 0.5 }} />;
    } else {
      return <HelpOutline fontSize="small" sx={{ color: theme.palette.grey[500], mr: 0.5 }} />;
    }
  };

    // New helper function to render historical trend ratings from trend reports
    // const renderHistoricalTrendRating = (reportType: 'Performance' | 'Risk' | 'Money Flow', currentData: any) => {
    //   if (!historicalReports || Object.keys(historicalReports).length === 0) return null;
      
    //   // Get dates in ascending order (oldest to newest)
    //   const dates = Object.keys(historicalReports).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
      
    //   // Take up to 20 most recent dates
    //   const recentDates = dates.slice(Math.max(0, dates.length - 20));
      
    //   return (
    //     <RatingHistoryContainer>
    //       {recentDates.map((date) => {
    //         const histReport = historicalReports[date]?.[reportType]?.MarketAllUp?.Report;
    //         if (!histReport) return null;
            
    //         const rating = histReport.Rating;
    //         if (!rating) return null;
            
    //         return (
    //           <Tooltip 
    //             key={date} 
    //             title={`${formatDate(date)}: ${rating}`}
    //             arrow
    //             placement="top"
    //           >
    //             <RatingHistoryDot color={getStatusColor(rating)} />
    //           </Tooltip>
    //         );
    //       })}
    //     </RatingHistoryContainer>
    //   );
    // };

    // New styled component for rating history tooltip container
// const RatingHistoryContainer = styled(Box)({
//   display: 'flex',
//   alignItems: 'center',
//   justifyContent: 'center',
//   marginTop: 6,
//   height: 10, // Keep height consistent
// });

// // New styled component for rating history dots
// const RatingHistoryDot = styled('div')(({ color }) => ({
//   width: 4,
//   height: 4,
//   borderRadius: '50%',
//   backgroundColor: color,
//   margin: '0 1px',
// }));

//     const renderHistoricalTrendRating = (reportType: 'Performance' | 'Risk' | 'Money Flow', currentData: any) => {
//       if (!currentData || Object.keys(currentData).length === 0) return null;
      
//       const dates = Object.keys(currentData).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
      
//       const recentDates = dates.slice(Math.max(0, dates.length - 20));

//       return (
//         <RatingHistoryContainer>
//           {recentDates.map((date) => {
//             const histReport = currentData[date]?.[reportType]?.MarketAllUp?.Report;
//             if (!histReport) return null;
            
//             const rating = histReport.Rating;
//             if (!rating) return null;
            
//             return (
//               <Tooltip 
//                 key={date} 
//                 title={`${formatDate(date)}: ${rating}`}
//                 arrow
//                 placement="top"
//               >
//                 <RatingHistoryDot color={getStatusColor(rating)} />
//               </Tooltip>
//             );
//           })}
//         </RatingHistoryContainer>
//       );
//     };
      
    


  // Safer check for required data
  const hasMinimumData = marketReport && 
                        (Object.keys(performanceData).length > 0 || 
                         Object.keys(riskData).length > 0 || 
                         Object.keys(moneyFlowData).length > 0);
  
  if (!hasMinimumData) {
    return (
      <Box>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
          Market Trends Analysis
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 3 }}>
          Incomplete market trend data available. Some sections may not display properly.
        </Alert>
        
        <Typography variant="body1">
          The market trends analysis requires performance, risk, and money flow data.
          Please refresh the page or try again later.
        </Typography>
      </Box>
    );
  }

  // Determine which content to show based on view prop
  const currentTabValue = view === 'performance' ? 0 : view === 'risk-level' ? 1 : view === 'money-flow' ? 2 : initialTab || 0;

  return (
    <Box>
      {/* Performance Tab */}
      {currentTabValue === 0 && (
        <Box>
          {performanceData && Object.keys(performanceData).length > 0 ? (
            <Grid container spacing={3}>
              {/* Overall Performance Card */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, mb: 3 }}>
                  
                <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUp sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
                    Overall Market Performance
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography>
                      Current Status
                      <Typography color={getSignalColor(performanceData?.MarketAllUp?.Report?.Rating || 'N/A')}> 
                        {performanceData?.MarketAllUp?.Report?.Rating || 'N/A'}
                      </Typography>
                      {/* Need to add historical ratingss */}
                    </Typography>

                    <Typography>
                      Trend
                      <Typography>
                        <Chip
                          icon={getTrendIcon(performanceData?.MarketAllUp?.Report?.Trend || 'N/A') || undefined}
                          label={performanceData?.MarketAllUp?.Report?.Trend || 'N/A'}
                          size="small"
                          sx={{
                            color: theme.palette.secondary.contrastText,
                            backgroundColor: getSignalColor(performanceData?.MarketAllUp?.Report?.Trend || 'N/A'),
                            '& .MuiChip-icon': {
                              color: theme.palette.secondary.contrastText,
                            },
                          }}
                        />
                      </Typography>
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body1">
                      <ReactMarkdown>
                        {performanceData?.MarketAllUp?.Report?.RatingTrendExplanation || 'No performance data available.'}
                      </ReactMarkdown>
                    </Typography>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Short-term Projection
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getTrendIcon(performanceData?.MarketAllUp?.Report?.ProjectedTrend_ShortTerm)}
                            <Typography variant="body1" fontWeight="medium">
                              {performanceData?.MarketAllUp?.Report?.ProjectedTrend_ShortTerm || 'N/A'}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Mid-term Projection
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getTrendIcon(performanceData?.MarketAllUp?.Report?.ProjectedTrend_MidTerm)}
                            <Typography variant="body1" fontWeight="medium">
                              {performanceData?.MarketAllUp?.Report?.ProjectedTrend_MidTerm || 'N/A'}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Long-term Projection
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getTrendIcon(performanceData?.MarketAllUp?.Report?.ProjectedTrend_LongTerm)}
                            <Typography variant="body1" fontWeight="medium">
                              {performanceData?.MarketAllUp?.Report?.ProjectedTrend_LongTerm || 'N/A'}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              
              {/* Market Cap Performance */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <MarketCapIcon sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
                    Performance by Market Cap
                  </Typography>
                  
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Market Cap</TableCell>
                          <TableCell>Rating</TableCell>
                          <TableCell>Trend</TableCell>
                          <TableCell>Short-term</TableCell>
                          <TableCell>Mid-term</TableCell>
                          <TableCell>Long-term</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {performanceData?.MarketCaps?.Reports?.map((cap: any, index: number) => (
                          <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              {cap.Classification.replace('MarketCap: ', '')}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={cap.Rating}
                                size="small"
                                // color={
                                //   cap.Rating.includes('LOSS') ? 'error' : 
                                //   cap.Rating.includes('GAIN') ? 'success' : 'default'
                                // }
                                sx={{
                                  color: theme.palette.secondary.contrastText,
                                  backgroundColor: getSignalColor(cap.Rating)
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {getTrendIcon(cap.Trend)}
                                <Typography variant="body2">
                                  {cap.Trend}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {getTrendIcon(cap.ProjectedTrend_ShortTerm)}
                                <Typography variant="body2">
                                  {cap.ProjectedTrend_ShortTerm}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {getTrendIcon(cap.ProjectedTrend_MidTerm)}
                                <Typography variant="body2">
                                  {cap.ProjectedTrend_MidTerm}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {getTrendIcon(cap.ProjectedTrend_LongTerm)}
                                <Typography variant="body2">
                                  {cap.ProjectedTrend_LongTerm}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
              
              {/* Sector Performance - New section */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <SectorIcon sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
                    Performance by Sector
                  </Typography>
                  
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Sector</TableCell>
                          <TableCell>Rating</TableCell>
                          <TableCell>Trend</TableCell>
                          <TableCell>Short-term (%)</TableCell>
                          <TableCell>Mid-term (%)</TableCell>
                          <TableCell>Long-term (%)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {performanceData?.Sectors?.Reports?.map((sector: any, index: number) => (
                          <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              {sector.Classification.replace('Sector: ', '')}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={sector.Rating}
                                size="small"
                                sx={{
                                  color: theme.palette.secondary.contrastText,
                                  backgroundColor: getSignalColor(sector.Rating)
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {getTrendIcon(sector.Trend)}
                                <Typography variant="body2">
                                  {sector.Trend}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{
                              color: parseFloat(sector.ShortTerm) >= 0 ? theme.palette.success.main : theme.palette.error.main,
                              fontWeight: 'medium'
                            }}>
                              {typeof sector.ShortTerm === 'number' || !isNaN(parseFloat(sector.ShortTerm)) 
                                ? formatPercent(parseFloat(sector.ShortTerm))
                                : 'N/A'}
                            </TableCell>
                            <TableCell sx={{
                              color: parseFloat(sector.MidTerm) >= 0 ? theme.palette.success.main : theme.palette.error.main,
                              fontWeight: 'medium'
                            }}>
                              {typeof sector.MidTerm === 'number' || !isNaN(parseFloat(sector.MidTerm)) 
                                ? formatPercent(parseFloat(sector.MidTerm))
                                : 'N/A'}
                            </TableCell>
                            <TableCell sx={{
                              color: parseFloat(sector.LongTerm) >= 0 ? theme.palette.success.main : theme.palette.error.main,
                              fontWeight: 'medium'
                            }}>
                              {typeof sector.LongTerm === 'number' || !isNaN(parseFloat(sector.LongTerm)) 
                                ? formatPercent(parseFloat(sector.LongTerm))
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Sector details cards */}
                  <Grid container spacing={2} sx={{ mt: 3 }}>
                    {performanceData?.Sectors?.Reports?.map((sector: any, index: number) => (
                      <Grid item xs={12} md={6} lg={4} key={`detail-${index}`}>
                        <Card sx={{ 
                          height: '100%',
                          borderLeft: `5px solid ${
                            getSignalColor(sector.Rating)
                          }`,
                        }}>
                          <CardContent>
                            <Typography variant="h6" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span>{sector.Classification.replace('Sector: ', '')}</span>
                              <Chip 
                                label={sector.Rating}
                                size="small"
                                sx={{
                                  color: theme.palette.secondary.contrastText,
                                  backgroundColor: getSignalColor(sector.Rating)
                                }}
                              />
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="body2" paragraph>
                              {sector.RatingTrendExplanation || 'No explanation available.'}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
              
              {/* Performance Recommendations */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <RecommendationsIcon sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
                    Performance-Based Recommendations
                  </Typography>
                  
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: theme.palette.background.paper, 
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    mb: 2
                  }}>
                    <ReactMarkdown>
                      {performanceData?.Recommendations || 'No performance recommendations available.'}
                    </ReactMarkdown>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info">No performance data available.</Alert>
          )}
        </Box>
      )}

      {/* Risk Analysis Tab */}
      {currentTabValue === 1 && (
        <Box>
          {riskData && Object.keys(riskData).length > 0 ? (
            <Grid container spacing={3}>
              {/* Overall Risk Card */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, mb: 3 }}>
                  
                  <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                    <ReportProblem sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
                    Overall Market Risk
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography>
                      Current Status
                      <Typography color={getSignalColor(riskData?.MarketAllUp?.Report?.Rating || 'N/A')}> 
                        {riskData?.MarketAllUp?.Report?.Rating || 'N/A'}
                      </Typography>
                      {/* Need to add historical ratingss */}
                    </Typography>

                    <Typography>
                      Trend
                      <Typography>
                        <Chip
                          icon={getTrendIcon(riskData?.MarketAllUp?.Report?.Trend || 'N/A') || undefined}
                          label={riskData?.MarketAllUp?.Report?.Trend || 'N/A'}
                          size="small"
                          sx={{
                            color: theme.palette.secondary.contrastText,
                            backgroundColor: getSignalColor(riskData?.MarketAllUp?.Report?.Trend || 'N/A'),
                            '& .MuiChip-icon': {
                              color: theme.palette.secondary.contrastText,
                            },
                          }}
                        />
                      </Typography>
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body1">
                      <ReactMarkdown>
                        {riskData?.MarketAllUp?.Report?.RatingTrendExplanation || 'No risk data available.'}
                      </ReactMarkdown>
                    </Typography>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Short-term Risk Projection
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getTrendIcon(riskData?.MarketAllUp?.Report?.ProjectedTrend_ShortTerm || 'NEUTRAL')}
                            <Typography variant="body1" fontWeight="medium">
                              {riskData?.MarketAllUp?.Report?.ProjectedTrend_ShortTerm || 'N/A'}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Mid-term Risk Projection
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getTrendIcon(riskData?.MarketAllUp?.Report?.ProjectedTrend_MidTerm || 'NEUTRAL')}
                            <Typography variant="body1" fontWeight="medium">
                              {riskData?.MarketAllUp?.Report?.ProjectedTrend_MidTerm || 'N/A'}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Long-term Risk Projection
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getTrendIcon(riskData?.MarketAllUp?.Report?.ProjectedTrend_LongTerm || 'NEUTRAL')}
                            <Typography variant="body1" fontWeight="medium">
                              {riskData?.MarketAllUp?.Report?.ProjectedTrend_LongTerm || 'N/A'}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              
              {/* Risk Levels by Market Cap */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <MarketCapIcon sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
                    Risk by Market Cap
                  </Typography>
                  
                  {(riskData?.MarketCaps?.Reports || riskData?.Reports || []).map((cap: any, index: number) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {cap.Classification?.replace('MarketCap: ', '') || 
                           cap.name?.replace('MarketCap: ', '') || 
                           cap.Name?.replace('MarketCap: ', '') || 
                           `Market Cap ${index + 1}`}
                        </Typography>
                        <Chip 
                          label={cap.Rating || 'N/A'}
                          size="small"
                          sx={{
                            color: theme.palette.secondary.contrastText,
                            backgroundColor: getSignalColor(cap.Rating)
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          Trend:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getTrendIcon(cap.Trend || 'NEUTRAL')}
                          <Typography variant="body2">
                            {cap.Trend || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                    </Box>
                  ))}
                  
                  {(riskData?.MarketCaps?.Reports || riskData?.Reports || []).length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No market cap risk data available
                    </Typography>
                  )}
                </Paper>
              </Grid>
              
              {/* Risk Levels by Sector - New section */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                    <SectorIcon sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
                    Risk by Sector
                  </Typography>
                  
                  {(riskData?.Sectors?.Reports || []).map((sector: any, index: number) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {sector.Classification?.replace('Sector: ', '') || 
                           sector.name?.replace('Sector: ', '') || 
                           sector.Name?.replace('Sector: ', '') || 
                           `Sector ${index + 1}`}
                        </Typography>
                        <Chip 
                          label={sector.Rating || 'N/A'}
                          size="small"
                          sx={{
                            color: theme.palette.secondary.contrastText,
                            backgroundColor: getSignalColor(sector.Rating)
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          Trend:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getTrendIcon(sector.Trend || 'NEUTRAL')}
                          <Typography variant="body2">
                            {sector.Trend || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                    </Box>
                  ))}
                  
                  {(riskData?.Sectors?.Reports || []).length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No sector risk data available
                    </Typography>
                  )}
                </Paper>
              </Grid>
              
              {/* Detailed Sector Risk Analysis */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <SectorIcon sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
                    Detailed Sector Risk Analysis
                  </Typography>
                  
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Sector</TableCell>
                          <TableCell>Risk Rating</TableCell>
                          <TableCell>Risk Trend</TableCell>
                          <TableCell>Short-term Outlook</TableCell>
                          <TableCell>Mid-term Outlook</TableCell>
                          <TableCell>Long-term Outlook</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(riskData?.Sectors?.Reports || []).map((sector: any, index: number) => (
                          <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              {sector.Classification?.replace('Sector: ', '') || 
                               sector.name?.replace('Sector: ', '') || 
                               sector.Name?.replace('Sector: ', '') || 
                               `Sector ${index + 1}`}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={sector.Rating || 'N/A'}
                                size="small"
                                sx={{
                                  color: theme.palette.secondary.contrastText,
                                  backgroundColor: getSignalColor(sector.Rating)
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {getTrendIcon(sector.Trend || 'NEUTRAL')}
                                <Typography variant="body2">
                                  {sector.Trend || 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {getTrendIcon(sector.ProjectedTrend_ShortTerm || 'NEUTRAL')}
                                <Typography variant="body2">
                                  {sector.ProjectedTrend_ShortTerm || 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {getTrendIcon(sector.ProjectedTrend_MidTerm || 'NEUTRAL')}
                                <Typography variant="body2">
                                  {sector.ProjectedTrend_MidTerm || 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {getTrendIcon(sector.ProjectedTrend_LongTerm || 'NEUTRAL')}
                                <Typography variant="body2">
                                  {sector.ProjectedTrend_LongTerm || 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
              
              {/* Risk Recommendations */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                    <RecommendationsIcon sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
                    Risk-Based Recommendations
                  </Typography>
                  
                  <Box sx={{ 
                    p: 2, 
                    mt: 2,
                    bgcolor: theme.palette.background.paper, 
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`
                  }}>
                    <ReactMarkdown>
                      {riskData?.Recommendations || 
                       riskData?.MarketAllUp?.Recommendations ||
                       riskData?.MarketAllUp?.Report?.Recommendations ||
                       'No risk recommendations available.'}
                    </ReactMarkdown>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info">No risk analysis data available.</Alert>
          )}
        </Box>
      )}

      {/* Money Flow Tab */}
      {currentTabValue === 2 && (
        <Box>
          {moneyFlowData && Object.keys(moneyFlowData).length > 0 ? (
            <Grid container spacing={3}>
              {/* Overall Money Flow Card */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, mb: 3 }}>
                  
                <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                    <AttachMoney sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
                    Overall Money Flow
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography>
                      Current Status
                      <Typography color={getSignalColor(moneyFlowData?.MarketAllUp?.Report?.Rating || 'N/A')}> 
                        {moneyFlowData?.MarketAllUp?.Report?.Rating || 'N/A'}
                      </Typography>
                      {/* Need to add historical ratingss */}
                    </Typography>

                    <Typography>
                      Trend
                      <Typography>
                        <Chip
                          icon={getTrendIcon(moneyFlowData?.MarketAllUp?.Report?.Trend || 'N/A') || undefined}
                          label={moneyFlowData?.MarketAllUp?.Report?.Trend || 'N/A'}
                          size="small"
                          sx={{
                            color: theme.palette.secondary.contrastText,
                            backgroundColor: getSignalColor(moneyFlowData?.MarketAllUp?.Report?.Trend || 'N/A'),
                            '& .MuiChip-icon': {
                              color: theme.palette.secondary.contrastText,
                            },
                          }}
                        />
                      </Typography>
                    </Typography>
                  </Box>

                  {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                      <MonetizationOn sx={{ mr: 1, color: theme.palette.custom.iconPrimary }} />
                      Overall Money Flow
                    </Typography>
                    <TrendBadge status={moneyFlowData?.MarketAllUp?.Report?.Rating || 'N/A'}>
                      {moneyFlowData?.MarketAllUp?.Report?.Rating || 'N/A'}
                      {getTrendIcon(moneyFlowData?.MarketAllUp?.Report?.Trend || 'NEUTRAL')}
                      {moneyFlowData?.MarketAllUp?.Report?.Trend || 'N/A'}
                    </TrendBadge>
                  </Box> */}
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body1">
                      <ReactMarkdown>
                        {moneyFlowData?.MarketAllUp?.Report?.RatingTrendExplanation || 'No money flow data available.'}
                      </ReactMarkdown>
                    </Typography>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Short-term Flow Projection
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getTrendIcon(moneyFlowData?.MarketAllUp?.Report?.ProjectedTrend_ShortTerm || 'NEUTRAL')}
                            <Typography variant="body1" fontWeight="medium">
                              {moneyFlowData?.MarketAllUp?.Report?.ProjectedTrend_ShortTerm || 'N/A'}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Mid-term Flow Projection
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getTrendIcon(moneyFlowData?.MarketAllUp?.Report?.ProjectedTrend_MidTerm || 'NEUTRAL')}
                            <Typography variant="body1" fontWeight="medium">
                              {moneyFlowData?.MarketAllUp?.Report?.ProjectedTrend_MidTerm || 'N/A'}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Long-term Flow Projection
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getTrendIcon(moneyFlowData?.MarketAllUp?.Report?.ProjectedTrend_LongTerm || 'NEUTRAL')}
                            <Typography variant="body1" fontWeight="medium">
                              {moneyFlowData?.MarketAllUp?.Report?.ProjectedTrend_LongTerm || 'N/A'}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              
              {/* Money Flow by Market Cap - New section */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                    <MarketCapIcon sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
                    Money Flow by Market Cap
                  </Typography>
                  
                  <TableContainer sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 'bold', bgcolor: theme.palette.primary.light, color: theme.palette.primary.contrastText } }}>
                          <TableCell>Market Cap</TableCell>
                          <TableCell>Flow Status</TableCell>
                          <TableCell>Trend</TableCell>
                          <TableCell>Short-term</TableCell>
                          <TableCell>Mid-term</TableCell>
                          <TableCell>Long-term</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(moneyFlowData?.MarketCaps?.Reports || []).map((cap: any, index: number) => (
                          <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              {cap.Classification?.replace('MarketCap: ', '') || 
                               cap.name?.replace('MarketCap: ', '') || 
                               cap.Name?.replace('MarketCap: ', '') || 
                               `Market Cap ${index + 1}`}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={cap.Rating || 'N/A'}
                                size="small"
                                sx={{
                                  color: theme.palette.secondary.contrastText,
                                  backgroundColor: getSignalColor(cap.Rating)
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {getTrendIcon(cap.Trend || 'NEUTRAL')}
                                <Typography variant="body2">
                                  {cap.Trend || 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {getTrendIcon(cap.ProjectedTrend_ShortTerm || 'NEUTRAL')}
                                <Typography variant="body2">
                                  {cap.ProjectedTrend_ShortTerm || 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {getTrendIcon(cap.ProjectedTrend_MidTerm || 'NEUTRAL')}
                                <Typography variant="body2">
                                  {cap.ProjectedTrend_MidTerm || 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {getTrendIcon(cap.ProjectedTrend_LongTerm || 'NEUTRAL')}
                                <Typography variant="body2">
                                  {cap.ProjectedTrend_LongTerm || 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <Grid container spacing={2}>
                    {(moneyFlowData?.MarketCaps?.Reports || []).map((cap: any, index: number) => (
                      <Grid item xs={12} sm={6} md={3} key={index}>
                        <StyledCard>
                          <CardContent>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                              {cap.Classification?.replace('MarketCap: ', '') || 
                               cap.name?.replace('MarketCap: ', '') || 
                               cap.Name?.replace('MarketCap: ', '') || 
                               `Market Cap ${index + 1}`}
                            </Typography>
                            
                            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2">Flow Status:</Typography>
                              <Chip 
                                label={cap.Rating || 'N/A'}
                                size="small"
                                sx={{
                                  color: theme.palette.secondary.contrastText,
                                  backgroundColor: getSignalColor(cap.Rating)
                                }}
                              />
                            </Box>
                            
                            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2">Trend:</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {getTrendIcon(cap.Trend || 'NEUTRAL')}
                                <Typography variant="body2">
                                  {cap.Trend || 'N/A'}
                                </Typography>
                              </Box>
                            </Box>
                            
                            <Tooltip title={cap.RatingTrendExplanation || 'No explanation available'}>
                              <Button 
                                variant="text" 
                                color="primary" 
                                size="small" 
                                endIcon={<Info />}
                                fullWidth
                              >
                                View Analysis
                              </Button>
                            </Tooltip>
                          </CardContent>
                        </StyledCard>
                      </Grid>
                    ))}
                    
                    {(moneyFlowData?.MarketCaps?.Reports || []).length === 0 && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          No market cap money flow data available
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
              
              {/* Money Flow by Sector */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <SectorIcon sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
                    Money Flow by Sector
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {(moneyFlowData?.Sectors?.Reports || moneyFlowData?.Reports || []).map((sector: any, index: number) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <StyledCard>
                          <CardContent>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                              {sector.Classification?.replace('Sector: ', '') || 
                               sector.name?.replace('Sector: ', '') || 
                               sector.Name?.replace('Sector: ', '') || 
                               `Sector ${index + 1}`}
                            </Typography>
                            
                            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2">Flow Status:</Typography>
                              <Chip 
                                label={sector.Rating || 'N/A'}
                                size="small"
                                sx={{
                                  color: theme.palette.secondary.contrastText,
                                  backgroundColor: getSignalColor(sector.Rating)
                                }}
                              />
                            </Box>
                            
                            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2">Trend:</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {getTrendIcon(sector.Trend || 'NEUTRAL')}
                                <Typography variant="body2">
                                  {sector.Trend || 'N/A'}
                                </Typography>
                              </Box>
                            </Box>
                            
                            <Tooltip title={sector.RatingTrendExplanation || 'No explanation available'}>
                              <Button 
                                variant="text" 
                                color="primary" 
                                size="small" 
                                endIcon={<Info />}
                                fullWidth
                              >
                                View Analysis
                              </Button>
                            </Tooltip>
                          </CardContent>
                        </StyledCard>
                      </Grid>
                    ))}
                    
                    {(moneyFlowData?.Sectors?.Reports || moneyFlowData?.Reports || []).length === 0 && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          No sector money flow data available
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
              
              {/* Money Flow Recommendations */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <RecommendationsIcon sx={{ mr: 1, color: theme.palette.custom.iconSecondary }} />
                    Money Flow Recommendations
                  </Typography>
                  
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: theme.palette.background.paper, 
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    mb: 2
                  }}>
                    <ReactMarkdown>
                      {moneyFlowData?.Recommendations || 
                       moneyFlowData?.MarketAllUp?.Recommendations ||
                       moneyFlowData?.MarketAllUp?.Report?.Recommendations ||
                       'No money flow recommendations available.'}
                    </ReactMarkdown>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info">No money flow data available.</Alert>
          )}
        </Box>
      )}
    </Box>
  );
};

export default MarketTrendsView; 